/**
 * Download a URL as a same-origin blob so the browser's built-in `<a
 * download>` attribute is respected.
 *
 * Direct `<a href=...>` clicks to a cross-origin URL don't respect the
 * `download` attribute on mobile Chrome — instead they open the URL in
 * a Chrome Custom Tab (visible in the screenshot: `X`-to-close bar +
 * address bar + in-app audio preview player). Fetching the audio into
 * a Blob and pointing `<a>` at the resulting `blob:` URL sidesteps this
 * entirely: the anchor is now same-origin, `download` triggers a native
 * download, and no browser navigates.
 *
 * Trade-off: whole file lives in memory during the download. For
 * MixGenie's ~3-4MB songs that's fine on any modern phone.
 */
export async function downloadBlobFromUrl(url: string, filename: string): Promise<void> {
  // Cache-bust the request: the <audio> element pre-loads the same URL with
  // mode: no-cors, and both the browser HTTP cache AND workbox cache the
  // response as OPAQUE. A subsequent cors-mode fetch is then served that
  // opaque entry and throws "TypeError: Failed to fetch". Appending a fresh
  // query param produces a distinct cache key and forces a real CORS fetch.
  const sep = url.includes('?') ? '&' : '?';
  const fetchUrl = `${url}${sep}_dl=${Date.now()}`;
  const res = await fetch(fetchUrl, { credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`Download failed: HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}
